import { useState, useRef, useEffect, useCallback } from 'react';

const PADDING = { top: 20, right: 10, bottom: 30, left: 50 };

export default function MiniLineChart({
  data,
  color = '#6366f1',
  height = 200,
  yFormat = (v) => v.toFixed(1),
  showDots = true,
}) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(350);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver((entries) => {
      setWidth(entries[0].contentRect.width);
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (!data || data.length === 0) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const chartW = width - PADDING.left - PADDING.right;
      const step = data.length > 1 ? chartW / (data.length - 1) : chartW;
      const idx = Math.round((mx - PADDING.left) / step);
      const clamped = Math.max(0, Math.min(data.length - 1, idx));
      const d = data[clamped];
      setTooltip({
        x: PADDING.left + clamped * step,
        y: 0,
        text: `${d.label || d.x}: ${yFormat(d.y)}`,
        subtext: d.date || '',
      });
    },
    [data, width, yFormat]
  );

  if (!data || data.length === 0) {
    return (
      <div ref={containerRef} style={{ width: '100%', height }}>
        <svg width="100%" height={height}>
          <text x="50%" y="50%" textAnchor="middle" fill="#8b8fa3" fontSize="12">
            No data
          </text>
        </svg>
      </div>
    );
  }

  const chartW = width - PADDING.left - PADDING.right;
  const chartH = height - PADDING.top - PADDING.bottom;

  const yValues = data.map((d) => d.y);
  const yMin = Math.min(...yValues);
  const yMax = Math.max(...yValues);
  const yRange = yMax - yMin || 1;

  const toX = (i) => PADDING.left + (data.length > 1 ? (i / (data.length - 1)) * chartW : chartW / 2);
  const toY = (v) => PADDING.top + chartH - ((v - yMin) / yRange) * chartH;

  const points = data.map((d, i) => `${toX(i)},${toY(d.y)}`).join(' ');

  // Y-axis: 4 ticks
  const yTicks = Array.from({ length: 5 }, (_, i) => yMin + (yRange * i) / 4);

  // Grid lines
  const gridLines = yTicks.map((v) => toY(v));

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setTooltip(null)}
    >
      <svg width={width} height={height}>
        {/* Grid */}
        {gridLines.map((y, i) => (
          <g key={i}>
            <line x1={PADDING.left} x2={width - PADDING.right} y1={y} y2={y} stroke="#1e2030" strokeWidth="1" />
            <text x={PADDING.left - 6} y={y + 4} textAnchor="end" fill="#8b8fa3" fontSize="10">
              {yFormat(yTicks[i])}
            </text>
          </g>
        ))}

        {/* X-axis labels (first, middle, last) */}
        {data.length > 0 && (
          <>
            <text x={toX(0)} y={height - 6} textAnchor="start" fill="#8b8fa3" fontSize="10">
              {data[0].x}
            </text>
            {data.length > 2 && (
              <text x={toX(Math.floor(data.length / 2))} y={height - 6} textAnchor="middle" fill="#8b8fa3" fontSize="10">
                {data[Math.floor(data.length / 2)].x}
              </text>
            )}
            <text x={toX(data.length - 1)} y={height - 6} textAnchor="end" fill="#8b8fa3" fontSize="10">
              {data[data.length - 1].x}
            </text>
          </>
        )}

        {/* Line */}
        <polyline fill="none" stroke={color} strokeWidth="2" points={points} />

        {/* Area fill */}
        <polygon
          fill={color}
          fillOpacity="0.08"
          points={`${toX(0)},${toY(yMin)} ${points} ${toX(data.length - 1)},${toY(yMin)}`}
        />

        {/* Dots */}
        {showDots &&
          data.map((d, i) => (
            <circle key={i} cx={toX(i)} cy={toY(d.y)} r="3" fill={color} />
          ))}

        {/* Tooltip */}
        {tooltip && (
          <g>
            <line x1={tooltip.x} x2={tooltip.x} y1={PADDING.top} y2={PADDING.top + chartH} stroke="#8b8fa3" strokeWidth="1" strokeDasharray="3,3" />
            <rect x={tooltip.x - 60} y={4} width="120" height={tooltip.subtext ? 30 : 18} rx="4" fill="#161822" stroke="#2a2d3a" />
            <text x={tooltip.x} y={16} textAnchor="middle" fill="#e1e4e8" fontSize="10" fontWeight="600">
              {tooltip.text}
            </text>
            {tooltip.subtext && (
              <text x={tooltip.x} y={28} textAnchor="middle" fill="#8b8fa3" fontSize="9">
                {tooltip.subtext}
              </text>
            )}
          </g>
        )}
      </svg>
    </div>
  );
}
